import { apiRequest, ApiError, type Envelope } from "../api/client";
import { environment } from "../env";

// PORT OF `SpeechToTextService` AND `SpeechAvailabilityService` (HOST-CRM #125, SERVER HALF
// CORE-API-V2 #118): RECORD THE MICROPHONE WITH `MediaRecorder`, THEN POST THE FINISHED
// RECORDING, RAW, TO THE CORE API'S `/speech-to-text/transcribe`. THE BROWSER TALKS TO THE
// CONFIGURED API AND NOTHING ELSE - THE AUDIO IS NEVER SENT ANYWHERE ELSE OR STORED.
//
// THE RULES KEPT FROM ANGULAR: EVERY RECORDING HAS A SESSION NUMBER, AND ANYTHING THAT
// ARRIVES FOR A SESSION THAT IS NO LONGER CURRENT (CANCELED, OR SUPERSEDED BY A NEW START) IS
// DROPPED; A RECORDING STOPS ITSELF AFTER TWO MINUTES AND IS TRANSCRIBED RATHER THAN LOST;
// AND A 503 MEANS THIS PROPERTY HAS NO ON-PREM TRANSCRIPTION, SO DICTATION IS WITHDRAWN FOR
// THE REST OF THE SESSION, APPLICATION-WIDE, INSTEAD OF FAILING ONCE PER ATTEMPT.

const TRANSCRIBE_PATH = "/speech-to-text/transcribe";
const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"];
const MAX_RECORDING_MS = 120_000;

// AVAILABILITY -------------------------------------------------------------------
// OPT-IN PER DEPLOYMENT (`speech_to_text: "true"` IN `env.js`), AND ONE ANSWER FOR THE WHOLE APP

let enabled = environment.speechToText;
const availabilityListeners = new Set<() => void>();

export const dictationAvailability = {
  get enabled() {
    return enabled;
  },
  subscribe(fn: () => void) {
    availabilityListeners.add(fn);
    return () => void availabilityListeners.delete(fn);
  },
  // NO WAY BACK ON PURPOSE: NOTHING CAN DEPLOY TRANSCRIPTION MID-SESSION
  disable() {
    if (!enabled) return;
    enabled = false;
    availabilityListeners.forEach((fn) => fn());
  },
};

// STATE -------------------------------------------------------------------------

export interface DictationState {
  isSupported: boolean;
  isEnabled: boolean;
  isAvailable: boolean;
  isStarting: boolean;
  isRecording: boolean;
  isStopping: boolean;
  isTranscribing: boolean;
  isBusy: boolean;
  permissionDenied: boolean;
  error: string | null;
}

export const MIC_DENIED = "Microphone access was denied. Please allow access to dictate.";
const CANNOT_RECORD = "This browser could not start an audio recording.";

export function unavailableReason(s: DictationState): string | null {
  if (s.isAvailable) return null;
  if (!s.isEnabled) return "Dictation is not available at this property. It needs on-prem transcription to be deployed.";
  return "This browser cannot record audio. Please use Chrome, Edge or Safari.";
}

// THE MESSAGE FOR A FAILED TRANSCRIPTION, BY STATUS; ONLY A 503 WITHDRAWS DICTATION
export function transcribeErrorMessage(status: number | undefined): string {
  if (status === 503) return "Dictation is not available at this property. It needs on-prem transcription to be deployed.";
  if (status === 400) return "No speech could be recognized. Please try again.";
  if (status === 413) return "That recording was too long. Please dictate a shorter passage.";
  return "Transcription failed. Please try again.";
}

const detectSupport = () =>
  typeof MediaRecorder !== "undefined" && typeof navigator !== "undefined" && !!navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === "function";

const pickMimeType = () => {
  if (typeof MediaRecorder.isTypeSupported !== "function") return "";
  return MIME_CANDIDATES.find((c) => MediaRecorder.isTypeSupported(c)) ?? "";
};

// ONE DICTATION SURFACE'S RECORDER. EACH TEXTAREA OWNS ITS OWN, SO NO FIELD CAN RECEIVE
// ANOTHER FIELD'S TRANSCRIPT.
export class Dictation {
  private state: DictationState;
  private session = 0;
  private recorder: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private mimeType = "";
  private recorderFailed = false;
  private maxTimer: number | null = null;
  private transcribeAbort: AbortController | null = null;
  private unsubscribe: () => void;

  constructor(
    private readonly onState: (s: DictationState) => void,
    private readonly onTranscript: (text: string) => void,
  ) {
    const isSupported = detectSupport();
    this.state = derive({
      isSupported,
      isEnabled: dictationAvailability.enabled,
      isAvailable: false,
      isStarting: false,
      isRecording: false,
      isStopping: false,
      isTranscribing: false,
      isBusy: false,
      permissionDenied: false,
      error: null,
    });
    // A WITHDRAWAL ELSEWHERE ABANDONS ANYTHING IN FLIGHT HERE, BUT KEEPS AN ERROR ON SCREEN
    this.unsubscribe = dictationAvailability.subscribe(() => {
      if (this.state.isBusy) this.cancel();
      this.update({ isEnabled: dictationAvailability.enabled });
    });
  }

  get current() {
    return this.state;
  }

  async start(): Promise<void> {
    if (!this.state.isAvailable) {
      this.update({ error: unavailableReason(this.state) });
      return;
    }
    // ALREADY BUSY - IGNORE RATHER THAN STACK A SECOND RECORDER ON THE MICROPHONE
    if (this.state.isBusy) return;

    const session = ++this.session;
    this.update({ isStarting: true, error: null });

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      // ABANDONED WHILE THE PERMISSION PROMPT WAS OPEN - STAY SILENT, THE CALLER IS GONE
      if (session !== this.session) return;
      this.update({ isStarting: false, permissionDenied: true, error: MIC_DENIED });
      return;
    }
    if (session !== this.session) {
      stream.getTracks().forEach((t) => t.stop());
      return;
    }

    this.stream = stream;
    this.chunks = [];
    this.recorderFailed = false;
    this.mimeType = pickMimeType();
    let recorder: MediaRecorder;
    try {
      recorder = this.mimeType ? new MediaRecorder(stream, { mimeType: this.mimeType }) : new MediaRecorder(stream);
    } catch {
      this.releaseStream();
      this.update({ isStarting: false, error: CANNOT_RECORD });
      return;
    }
    this.recorder = recorder;

    recorder.ondataavailable = (e: BlobEvent) => {
      if (session !== this.session) return;
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    recorder.onerror = () => {
      if (session !== this.session) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      this.clearMaxTimer();
      this.chunks = [];
      this.recorderFailed = true;
      this.releaseStream();
      this.update({ isRecording: false, error: "Audio recording failed. Please try again." });
    };
    recorder.onstop = () => this.onStopped(session, recorder, stream);

    try {
      recorder.start();
    } catch {
      this.recorder = null;
      this.releaseStream();
      this.update({ isStarting: false, error: CANNOT_RECORD });
      return;
    }

    // THE TWO-MINUTE CAP: A DELIBERATE, USER-FACING LIMIT, NOT A SEQUENCING DELAY
    this.maxTimer = window.setTimeout(() => {
      if (session === this.session) this.stop();
    }, MAX_RECORDING_MS);
    this.update({ isStarting: false, isRecording: true, permissionDenied: false, error: null });
  }

  stop(): void {
    const recorder = this.recorder;
    if (!recorder || !this.state.isRecording || recorder.state !== "recording") return;
    this.clearMaxTimer();
    const session = this.session;
    const stream = this.stream;
    this.update({ isRecording: false, isStopping: true });
    try {
      recorder.stop();
    } catch {
      this.onStopped(session, recorder, stream);
    }
  }

  // ABANDON WHATEVER IS IN FLIGHT: A RECORDING, OR A TRANSCRIPTION NOBODY IS WAITING FOR
  cancel(): void {
    this.session++;
    this.recorderFailed = false;
    this.clearMaxTimer();
    this.transcribeAbort?.abort();
    this.transcribeAbort = null;
    if (this.recorder && this.recorder.state === "recording") {
      try {
        this.recorder.stop();
      } catch {
        // ALREADY STOPPED
      }
    }
    this.chunks = [];
    this.recorder = null;
    this.releaseStream();
    this.update({ isStarting: false, isRecording: false, isStopping: false, isTranscribing: false, error: null, permissionDenied: false });
  }

  async requestPermission(): Promise<boolean> {
    if (!this.state.isSupported) return false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      this.update({ error: null, permissionDenied: false });
      return true;
    } catch {
      this.update({ permissionDenied: true, error: MIC_DENIED });
      return false;
    }
  }

  dispose(): void {
    this.cancel();
    this.unsubscribe();
  }

  private onStopped(session: number, recorder: MediaRecorder | null, stream: MediaStream | null) {
    stream?.getTracks().forEach((t) => t.stop());
    if (session !== this.session) return;
    this.clearMaxTimer();
    const chunks = this.chunks;
    const mimeType = this.mimeType || recorder?.mimeType || "audio/webm";
    this.chunks = [];
    this.recorder = null;
    this.stream = null;
    this.update({ isRecording: false, isStopping: false });
    if (this.recorderFailed) {
      this.recorderFailed = false;
      return;
    }
    const blob = new Blob(chunks, { type: mimeType });
    if (blob.size === 0) {
      this.update({ error: "No audio was captured. Please try again." });
      return;
    }
    void this.transcribe(blob, mimeType, session);
  }

  private async transcribe(blob: Blob, mimeType: string, session: number) {
    this.update({ isTranscribing: true, error: null });
    const abort = new AbortController();
    this.transcribeAbort = abort;
    try {
      const r = await apiRequest<Envelope<{ text?: string } | null> & { text?: string }>(TRANSCRIBE_PATH, {
        method: "POST",
        raw: { body: blob, contentType: mimeType },
        signal: abort.signal,
      });
      if (session !== this.session) return;
      this.transcribeAbort = null;
      const text = (r?.data?.text || r?.text || "").trim();
      this.update({ isTranscribing: false });
      // THE ENDPOINT ANSWERS 400 FOR SILENCE, BUT AN EMPTY 200 MUST NOT EMIT AN EMPTY TRANSCRIPT
      if (!text) {
        this.update({ error: "No speech could be recognized. Please try again." });
        return;
      }
      this.onTranscript(text);
    } catch (e) {
      if (session !== this.session) return;
      this.transcribeAbort = null;
      const status = e instanceof ApiError ? e.status : undefined;
      // SETTLE THIS FIELD'S MESSAGE BEFORE THE WITHDRAWAL, WHICH CANCELS BUSY SURFACES
      this.update({ isTranscribing: false, error: transcribeErrorMessage(status) });
      if (status === 503) dictationAvailability.disable();
    }
  }

  private releaseStream() {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private clearMaxTimer() {
    if (this.maxTimer !== null) window.clearTimeout(this.maxTimer);
    this.maxTimer = null;
  }

  private update(partial: Partial<DictationState>) {
    this.state = derive({ ...this.state, ...partial });
    this.onState(this.state);
  }
}

// `isAvailable` AND `isBusy` ARE DERIVED, SO NEITHER CAN DRIFT FROM THE FLAGS THEY DEPEND ON
function derive(s: DictationState): DictationState {
  return {
    ...s,
    isAvailable: s.isSupported && s.isEnabled,
    isBusy: s.isStarting || s.isRecording || s.isStopping || s.isTranscribing,
  };
}

// `web-speech-to-text.appendTranscript`: A SPACE BETWEEN, UNLESS THE TEXT ALREADY ENDS IN ONE,
// CUT TO THE FIELD'S LIMIT
export function appendTranscript(existing: string, transcript: string, maxLength: number): string {
  const separator = existing && !existing.endsWith(" ") ? " " : "";
  return (existing + separator + transcript).slice(0, maxLength);
}
